import { useState, useCallback, useMemo, useEffect } from 'react'
import { X, FolderPlus } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { getRootFolderId } from './driveRoot'
import { PickerNavigator, crumbsText, type PickerCrumb, type PickerRoot } from '@papan/files/ui/picker'

const NARROW = '(max-width: 639.98px)'

// Re-exported for backwards-compatible `./FolderPickerDialog` imports — the
// definitions live in `./driveRoot` so they can be imported without an
// import cycle (PickerNavigator needs `isRootFolderId`).
export { getRootFolderId, isRootFolderId } from './driveRoot'

interface FolderPickerDialogProps {
  tenantId: string
  onSelect: (folderId: string) => void
  onClose: () => void
  /** Folder IDs to hide from the picker (e.g. the workspace's own folder) */
  excludeIds?: string[]
  /** Dialog title (default: "Link Drive Folder" — workspace-link use case). */
  title?: string
  /** Confirm button label (default: "Link"). */
  confirmLabel?: string
}

/**
 * Pick a drive folder. Single-column drilldown (issue #21): folders drill
 * in on click, the currently-open folder is the selection target, and the
 * footer "Link" button acts on it. Search spans the whole permitted tree.
 *
 * `title` / `confirmLabel` are optional so other callers (e.g. bin
 * restore — issue #81) can reuse the picker with use-case-appropriate
 * copy without forking the component.
 */
export function FolderPickerDialog({
  tenantId, onSelect, onClose, excludeIds,
  title = 'Link Drive Folder',
  confirmLabel = 'Link',
}: FolderPickerDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const rootId = getRootFolderId(tenantId)

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  // On a phone the fixed 600px two-pane frame overflows — go full-width and
  // single-pane (the split browse layout is unusable at ~360px). Below the
  // navigator's own `sm` (640px) exactly, so the two never disagree at 640.
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW).matches)
  useEffect(() => {
    const mq = window.matchMedia(NARROW)
    const on = () => setIsNarrow(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])

  // The "selected folder" is whatever folder the navigator is currently
  // showing — drilling in selects it; the footer "Link" acts on it.
  const handleNavigate = useCallback((folderId: string | null, breadcrumbs: PickerCrumb[]) => {
    setSelectedId(folderId)
    // A list place (Projects, Shared) is no folder: nothing to pick until one opens.
    // The path reads as the navigator tells it ("My Space › Reports").
    setSelectedPath(folderId === null ? null : crumbsText(breadcrumbs))
  }, [])

  const roots = useMemo<PickerRoot[]>(() => [{ id: rootId, label: 'papan' }], [rootId])
  const hideItem = useCallback(
    (f: { id: string }) => !!excludeIds && excludeIds.includes(f.id),
    [excludeIds],
  )

  const handleConfirm = () => {
    if (selectedId) onSelect(selectedId)
  }

  return createPortal(
    // z-[100]: above the mobile header (z-[60]) so the overlay covers the full
    // viewport there — desktop is unaffected (no chrome sits in 50–100).
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog — 600x360. Same width as SaveAsDialog so the two
          pickers feel like one family; shorter because this picker
          has no filename / conflict / shortcut bands and the taller
          frame was mostly empty (issue #81 follow-up). */}
      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-full max-w-[600px] ${isNarrow ? 'max-h-[85vh]' : 'h-[360px] max-h-[90vh]'} flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <div className="flex items-center gap-2">
            <FolderPlus size={16} className="themed-text-secondary" />
            <h3 className="text-sm font-medium themed-text">{title}</h3>
          </div>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        {/* Navigator — splitView matches SaveAsDialog's two-pane
            browse layout (parent on the left, current on the right).
            Files are hidden in folder mode, so the panes only render
            folders; the left pane lets users hop between siblings
            without first navigating up. */}
        <PickerNavigator
          roots={roots}
          initialFolderId={rootId}
          mode="folder"
          selectedId={selectedId}
          onSelect={() => { /* folder mode — files are hidden, selection comes from onNavigate */ }}
          onNavigate={handleNavigate}
          hideItem={hideItem}
          splitView={!isNarrow}
          // Don't steal focus on phones — it pops the iOS keyboard over the list.
          autoFocusSearch={!isNarrow}
          className="flex-1 min-h-0"
        />

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t themed-border">
          <div className="text-xs themed-text-muted truncate max-w-[380px]" data-testid="folder-picker-selected-path">
            {selectedPath || 'Select a folder'}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedId}
              className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
